import { ChatMessage, ChatSession, StreamChunk, MCPServer, MCPTool, MCPServerMetadata, User, Workspace, SavedWorkspaceState, CreateSavedStateRequest, UpdateSavedStateRequest, BuildInfo, BoxAuthStatus, BoxAuthResponse } from '../types';
import { authService } from './auth';
import { validateInput, createSavedStateSchema, updateSavedStateSchema, chatMessageSchema } from './validation';

class ApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_URL || '';
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await authService.getToken();
    if (!token) {
      console.warn('No authentication token available');
    }
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async getAuthToken(): Promise<string | null> {
    return await authService.getToken();
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers = {
      'Content-Type': 'application/json',
      ...(await this.getAuthHeaders()),
      ...options.headers,
    };

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      // Handle 401 Unauthorized - token expired or invalid
      if (response.status === 401) {
        console.warn('API request failed with 401 - checking if token renewal is needed');
        
        // Try to renew the token silently first
        try {
          await authService.silentRenew();
          // Retry the request with the new token
          const newHeaders = {
            'Content-Type': 'application/json',
            ...(await this.getAuthHeaders()),
            ...options.headers,
          };
          
          const retryResponse = await fetch(`${this.baseUrl}${endpoint}`, {
            ...options,
            headers: newHeaders,
          });
          
          if (retryResponse.ok) {
            return retryResponse.json();
          }
        } catch (renewError) {
          console.error('Token renewal failed:', renewError);
        }
        
        // Only sign out if we're not on the callback page (to avoid logout loops)
        if (!window.location.pathname.includes('/callback')) {
          console.warn('Token renewal failed, signing out');
          await authService.signOut();
          return Promise.reject(new Error('Authentication required'));
        }
        
        return Promise.reject(new Error('Authentication failed'));
      }

      let errorText;
      try {
        errorText = await response.text();
      } catch (e) {
        errorText = 'Failed to read response';
      }
      
      let error;
      try {
        error = JSON.parse(errorText);
      } catch (e) {
        error = { message: errorText || 'Unknown error' };
      }
      
      console.error('API Error:', {
        status: response.status,
        message: error.message
      });
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async getCurrentUser(): Promise<User> {
    return this.request<{ user: User }>('/api/user').then(res => res.user);
  }

  // Build info methods
  async getBuildInfo(): Promise<BuildInfo> {
    const response = await fetch(`${this.baseUrl}/api/build-info`);
    if (!response.ok) {
      throw new Error(`Failed to fetch build info: ${response.status}`);
    }
    return response.json();
  }

  // Workspace methods
  async getWorkspaces(): Promise<Workspace[]> {
    return this.request<{ workspaces: Workspace[] }>('/api/workspaces')
      .then(res => res.workspaces);
  }

  async getWorkspace(id: string): Promise<Workspace> {
    return this.request<{ workspace: Workspace }>(`/api/workspaces/${id}`)
      .then(res => res.workspace);
  }


  // File upload method
  async uploadFiles(workspaceId: string, files: File[], message: string = ''): Promise<{
    success: boolean;
    files: Array<{ 
      name: string; 
      content: string; 
      type: string; 
      size: number;
      isBinary: boolean;
      displayName: string;
    }>;
    message: string;
    totalFiles: number;
    totalSize: number;
  }> {
    const formData = new FormData();
    
    files.forEach(file => {
      formData.append('files', file);
    });
    
    formData.append('message', message);
    formData.append('workspaceId', workspaceId);

    const authHeaders = await this.getAuthHeaders();
    
    const response = await fetch(`${this.baseUrl}/api/chat/upload`, {
      method: 'POST',
      headers: {
        ...authHeaders,
        // Don't set Content-Type, let browser set it with boundary for multipart
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'File upload failed' }));
      throw new Error(error.error || error.message || 'File upload failed');
    }

    return await response.json();
  }

  // Chat methods
  async *streamChat(
    workspaceId: string,
    messages: ChatMessage[],
    settings: {
      mcpServers?: string[];
      routeHint?: {
        domain?: string;
        costSensitivity?: string;
        dataSensitivity?: string;
      };
      aiName?: string;
      forceNewSession?: boolean;
    } = {}
  ): AsyncGenerator<StreamChunk, void, unknown> {
    // Use simple JSON response for now
    const authHeaders = await this.getAuthHeaders();
    
    // Check if we have authentication
    if (!authHeaders.Authorization) {
      throw new Error('Not authenticated - please sign in');
    }
    
    const response = await fetch(`${this.baseUrl}/api/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
      },
      body: JSON.stringify({
        workspaceId,
        messages: messages.map(msg => ({
          role: msg.role,
          content: msg.content,
          tool_call_id: msg.role === 'tool' ? msg.id : undefined,
        })),
        mcpServers: settings.mcpServers,
        routeHint: settings.routeHint,
        aiName: settings.aiName,
        forceNewSession: settings.forceNewSession,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      console.error('API Error:', {
        status: response.status,
        statusText: response.statusText,
        error: error,
        url: response.url
      });
      
      // Handle authentication errors by redirecting to login
      if (response.status === 401 && (error.code?.includes('JWT') || error.code?.includes('AUTH'))) {
        console.log('Authentication error detected, clearing auth and redirecting to login');
        // Clear authentication state
        await authService.signOut();
        return;
      }
      
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    // Handle Server-Sent Events streaming
    if (response.headers.get('content-type')?.includes('text/event-stream')) {
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      
      if (!reader) {
        throw new Error('No response body available');
      }

      let buffer = '';
      
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        
        // Process complete SSE messages
        const messages = buffer.split('\n\n');
        buffer = messages.pop() || ''; // Keep incomplete message in buffer
        
        for (const message of messages) {
          if (message.trim().startsWith('data: ')) {
            try {
              const jsonData = message.trim().substring(6); // Remove 'data: '
              const parsed = JSON.parse(jsonData);
              
              if (parsed.type === 'text' && parsed.delta) {
                yield { type: 'text', delta: parsed.delta };
              } else if (parsed.type === 'final') {
                yield { 
                  type: 'final', 
                  usage: parsed.usage,
                  metadata: { toolsUsed: parsed.toolsUsed }
                };
              } else if (parsed.type === 'metadata') {
                yield {
                  type: 'metadata',
                  metadata: {
                    provider: parsed.provider,
                    model: parsed.model,
                    workspace: parsed.workspace,
                    domain: parsed.domain
                  }
                };
              } else if (parsed.type === 'error') {
                throw new Error(parsed.error);
              }
            } catch (error) {
              console.error('Error parsing SSE message:', error);
            }
          }
        }
      }
    } else {
      // Fallback to JSON response for backward compatibility
      const data = await response.json();
      
      // Yield the response content
      yield { type: 'text', delta: data.content };
      
      if (data.usage) {
        yield { type: 'final', usage: data.usage };
      }
    }
  }

  // Chat session methods
  async getChatSessions(workspaceId: string): Promise<ChatSession[]> {
    return this.request<{ sessions: ChatSession[] }>(`/api/workspaces/${workspaceId}/sessions`)
      .then(res => res.sessions);
  }

  async createChatSession(workspaceId: string, title?: string): Promise<ChatSession> {
    return this.request<{ session: ChatSession }>(`/api/workspaces/${workspaceId}/sessions`, {
      method: 'POST',
      body: JSON.stringify({ title }),
    }).then(res => res.session);
  }

  async updateChatSession(sessionId: string, updates: Partial<ChatSession>): Promise<ChatSession> {
    return this.request<{ session: ChatSession }>(`/api/sessions/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }).then(res => res.session);
  }

  // MCP methods
  async getMCPServers(): Promise<MCPServer[]> {
    try {
      const result = await this.request<{ servers: MCPServer[] }>('/api/mcp/servers');
      return result.servers || [];
    } catch (error) {
      console.error('Failed to load MCP servers:', error);
      throw error;
    }
  }

  async getMCPTools(serverNames?: string[]): Promise<MCPTool[]> {
    const query = serverNames ? `?servers=${serverNames.join(',')}` : '';
    try {
      const result = await this.request<{ tools: MCPTool[] }>(`/api/mcp/tools${query}`);
      return result.tools || [];
    } catch (error) {
      console.error('Failed to load MCP tools:', error);
      throw error;
    }
  }

  // Health and monitoring
  async getHealth(): Promise<{ status: string; checks?: Record<string, any> }> {
    return this.request('/health');
  }

  async getAuditStats(filters: {
    userId?: string;
    startTime?: string;
    endTime?: string;
  } = {}): Promise<{
    totalRequests: number;
    successfulRequests: number;
    failedRequests: number;
    avgDuration: number;
  }> {
    const query = new URLSearchParams(filters).toString();
    return this.request(`/api/audit/stats${query ? `?${query}` : ''}`);
  }

  // Saved workspace states methods
  async getSavedStates(workspaceId?: string, autoOpenOnly?: boolean): Promise<SavedWorkspaceState[]> {
    const params = new URLSearchParams();
    if (workspaceId) params.set('workspaceId', workspaceId);
    if (autoOpenOnly) params.set('autoOpenOnly', 'true');
    
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<{ savedStates: SavedWorkspaceState[] }>(`/api/saved-states${query}`)
      .then(res => res.savedStates.map(state => ({
        ...state,
        createdAt: new Date(state.createdAt),
        updatedAt: new Date(state.updatedAt),
        lastAccessedAt: state.lastAccessedAt ? new Date(state.lastAccessedAt) : undefined,
      })));
  }

  async getSavedState(id: string): Promise<SavedWorkspaceState> {
    return this.request<{ savedState: SavedWorkspaceState }>(`/api/saved-states/${id}`)
      .then(res => ({
        ...res.savedState,
        createdAt: new Date(res.savedState.createdAt),
        updatedAt: new Date(res.savedState.updatedAt),
        lastAccessedAt: res.savedState.lastAccessedAt ? new Date(res.savedState.lastAccessedAt) : undefined,
      }));
  }

  async createSavedState(data: CreateSavedStateRequest): Promise<SavedWorkspaceState> {
    
    // Validate input data on frontend before sending to API
    let validatedData;
    try {
      validatedData = validateInput(createSavedStateSchema, data, 'savedState');
    } catch (error) {
      console.error('Frontend validation failed:', error);
      throw error;
    }
    
    try {
      const result = await this.request<{ savedState: SavedWorkspaceState }>('/api/saved-states', {
        method: 'POST',
        body: JSON.stringify(validatedData),
      });
      return {
        ...result.savedState,
        createdAt: new Date(result.savedState.createdAt),
        updatedAt: new Date(result.savedState.updatedAt),
        lastAccessedAt: result.savedState.lastAccessedAt ? new Date(result.savedState.lastAccessedAt) : undefined,
      };
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  async updateSavedState(id: string, data: UpdateSavedStateRequest): Promise<SavedWorkspaceState> {
    // Validate input data on frontend before sending to API
    const validatedData = validateInput(updateSavedStateSchema, data, 'savedState');
    
    return this.request<{ savedState: SavedWorkspaceState }>(`/api/saved-states/${id}`, {
      method: 'PUT',
      body: JSON.stringify(validatedData),
    }).then(res => ({
      ...res.savedState,
      createdAt: new Date(res.savedState.createdAt),
      updatedAt: new Date(res.savedState.updatedAt),
      lastAccessedAt: res.savedState.lastAccessedAt ? new Date(res.savedState.lastAccessedAt) : undefined,
    }));
  }

  async deleteSavedState(id: string): Promise<void> {
    await this.request(`/api/saved-states/${id}`, {
      method: 'DELETE',
    });
  }

  // Box OAuth methods
  async initiateBoxAuth(): Promise<BoxAuthResponse> {
    return this.request<BoxAuthResponse>('/api/oauth/box/initiate', {
      method: 'POST',
    });
  }

  async getBoxStatus(): Promise<BoxAuthStatus> {
    return this.request<BoxAuthStatus>('/api/oauth/box/status');
  }

  async disconnectBox(): Promise<void> {
    await this.request('/api/oauth/box/disconnect', {
      method: 'POST',
    });
  }

  // Theme Settings
  async getUserThemeSettings(): Promise<any> {
    return this.request('/api/user-preferences/theme');
  }

  async saveUserThemeSettings(settings: any): Promise<void> {
    await this.request('/api/user-preferences/theme', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  // MCP metadata method
  async getMCPMetadata(): Promise<MCPServerMetadata[]> {
    const response = await this.request<{ success: boolean; metadata: MCPServerMetadata[] }>('/api/mcp-metadata');
    return response.metadata || [];
  }
}

export const apiService = new ApiService();
export default apiService;